import { useEffect, useState } from "react"
import { Meeting, MeetingType } from "@/schema"
import { Badge, CustomSelectTrigger, Select, SelectContent, SelectItem } from "@ui/shad"
import { cn } from "@ui/utils"
import { ChevronDown, Loader2 } from "lucide-react"
import { toast } from "sonner"

import {
  activeMeetingTypes,
  findMeetingType,
  humanizeMeetingTypeKey,
  meetingTypeLabel,
  meetingTypeSwatchStyle,
  SYSTEM_MEETING_TYPE_ORDER,
} from "@/lib/meeting-types"
import { isMeetingPromptFinished } from "@/lib/prompt-status"
import { useMeetingTypes } from "@/hooks/api/meeting-types"
import { useGenerateReviewMaterials, useUpdateMeeting } from "@/hooks/api/meetings"
import { GenericDialog } from "@/components/dialogs"
import { ScheduledNotetakersDialog } from "@/components/notetaker-policy/scheduled-notetakers-dialog"

type MeetingWithType = Pick<
  Meeting,
  "id" | "meeting_type" | "prompt_statuses" | "skip_internal_meetings" | "scheduled_notetaker"
> & {
  has_ai_content?: boolean
}

interface MeetingTypeSelectorProps {
  meeting: MeetingWithType
  size?: "sm" | "default"
  className?: string
}

export default function MeetingTypeSelector({ meeting, size = "default", className }: MeetingTypeSelectorProps) {
  const [meetingType, setMeetingType] = useState<MeetingType>(meeting.meeting_type ?? "other")
  const { data: meetingTypesData } = useMeetingTypes()
  const { mutate: updateMeeting, isPending } = useUpdateMeeting()
  const { mutate: generateReviewMaterials, isPending: isReprocessing } = useGenerateReviewMaterials()
  const [scheduledOpen, setScheduledOpen] = useState(false)
  const [scheduledSelection, setScheduledSelection] = useState<string[]>()
  const [pendingType, setPendingType] = useState<MeetingType | null>(null)
  const closeDialog = () => setPendingType(null)

  useEffect(() => {
    setMeetingType(meeting.meeting_type ?? "other")
  }, [meeting.meeting_type])

  const hasAiContent =
    meeting.has_ai_content ??
    (isMeetingPromptFinished(meeting, "Prompt::MeetingQuickSummary") ||
      isMeetingPromptFinished(meeting, "Prompt::MeetingNote") ||
      isMeetingPromptFinished(meeting, "Prompt::MeetingTasks"))

  function updateType(
    selectedType: MeetingType,
    callbacks?: {
      onSuccess?: () => void
      onError?: () => void
    },
    selection = scheduledSelection
  ) {
    updateMeeting(
      { meetingId: meeting.id, data: { meeting_type: selectedType, cancel_scheduled_meeting_ids: selection } },
      {
        onSuccess: () => {
          setScheduledSelection(undefined)
          toast.success("Meeting type updated")
          callbacks?.onSuccess?.()
        },
        onError: () => {
          setMeetingType(meeting.meeting_type ?? "other")
          callbacks?.onError?.()
        },
      }
    )
  }

  function handleTypeChange(newType: string) {
    const selectedType = newType as MeetingType
    setMeetingType(selectedType)

    if (
      selectedType === "internal" &&
      meeting.meeting_type !== "internal" &&
      meeting.skip_internal_meetings &&
      meeting.scheduled_notetaker
    ) {
      setPendingType(selectedType)
      setScheduledOpen(true)
      return
    }

    if (!hasAiContent) {
      updateType(selectedType)
      return
    }

    setPendingType(selectedType)
  }

  const changeType = (reprocess: boolean) => {
    if (!pendingType || isPending || isReprocessing) return
    updateType(pendingType, {
      onSuccess: () => {
        if (!reprocess) {
          closeDialog()
          return
        }
        generateReviewMaterials(
          { meetingId: meeting.id, force: true },
          {
            onSuccess: () => {
              toast.success("Reprocessing notes and tasks")
              closeDialog()
            },
            onError: () => {
              setMeetingType(meeting.meeting_type ?? "other")
              closeDialog()
            },
          }
        )
      },
      onError: closeDialog,
    })
  }

  const fetchedMeetingTypes = meetingTypesData?.meeting_types
  const options =
    activeMeetingTypes(fetchedMeetingTypes, meetingType).length > 0
      ? activeMeetingTypes(fetchedMeetingTypes, meetingType)
      : SYSTEM_MEETING_TYPE_ORDER.map((key) => ({
          id: key,
          key,
          name: humanizeMeetingTypeKey(key),
          description: null,
          color: null,
          sort_order: 0,
          system: true,
          archived_at: null,
          created_at: new Date(),
          updated_at: new Date(),
        }))
  const selectedMeetingType = findMeetingType(fetchedMeetingTypes, meetingType)
  const selectedLabel = meetingTypeLabel(fetchedMeetingTypes, meetingType)

  return (
    <>
      <Select value={meetingType} onValueChange={handleTypeChange}>
        <CustomSelectTrigger asChild>
          <button
            className={cn(
              "bg-background text-secondary-foreground hover:bg-muted/80 inline-flex items-center gap-0.5 rounded-md border pr-1 pl-2 leading-none font-medium transition-colors",
              size === "sm" ? "h-6 text-xs" : "h-7 text-sm",
              className
            )}
            style={meetingTypeSwatchStyle(selectedMeetingType?.color)}
          >
            {selectedLabel}
            {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronDown className="size-3.5!" />}
          </button>
        </CustomSelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.key} value={option.key} className="hover:bg-tinted mr-8 p-1">
              <Badge
                variant="custom"
                size="base"
                className="bg-background text-secondary-foreground border-border"
                style={meetingTypeSwatchStyle(option.color)}
              >
                {option.name}
              </Badge>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ScheduledNotetakersDialog
        open={scheduledOpen}
        onOpenChange={(open) => {
          setScheduledOpen(open)
          if (!open) {
            setMeetingType(meeting.meeting_type ?? "other")
            closeDialog()
          }
        }}
        meetings={meeting.scheduled_notetaker ? [meeting.scheduled_notetaker] : []}
        pending={isPending}
        onConfirm={(selection) => {
          setScheduledSelection(selection)
          setScheduledOpen(false)
          if (!hasAiContent && pendingType) updateType(pendingType, { onSuccess: closeDialog }, selection)
        }}
      />
      <GenericDialog
        open={pendingType !== null && !scheduledOpen && hasAiContent}
        onOpenChange={(open) => {
          if (!open) {
            setMeetingType(meeting.meeting_type ?? "other")
            closeDialog()
          }
        }}
        title="Reprocess AI notes?"
        description="Changing the meeting type affects how AI notes and tasks are generated. Would you like to reprocess them with the new type?"
        dismiss={isPending || isReprocessing ? "none" : "all"}
        action={{
          label: "Reprocess notes & tasks",
          onClick: () => changeType(true),
          pending: isPending || isReprocessing,
        }}
        secondaryAction={{
          label: "Just change type",
          onClick: () => changeType(false),
          disabled: isPending || isReprocessing,
        }}
      />
    </>
  )
}

interface MeetingTypeBadgeProps {
  meetingType?: MeetingType
  size?: "sm" | "default"
}

export function MeetingTypeBadge({ meetingType = "other", size = "sm" }: MeetingTypeBadgeProps) {
  const { data: meetingTypesData } = useMeetingTypes()
  const type = findMeetingType(meetingTypesData?.meeting_types, meetingType)

  return (
    <span
      className={cn(
        "bg-background text-secondary-foreground border-border inline-flex items-center rounded-md border px-2 leading-none font-medium transition-colors",
        size === "sm" ? "h-6 text-xs" : "h-7 text-sm"
      )}
      style={meetingTypeSwatchStyle(type?.color)}
    >
      {meetingTypeLabel(meetingTypesData?.meeting_types, meetingType)}
    </span>
  )
}
